import React, { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  addHours,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameMonth,
  isToday,
  startOfHour,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Titulo from '../../components/tipografia/Titulo';
import Descricao from '../../components/tipografia/Descricao';
import VBox from '../../components/layout/VBox';
import HBox from '../../components/layout/HBox';
import Grid, { GridItem } from '../../components/layout/Grid';
import Card from '../../components/layout/Card';
import Modal from '../../components/layout/Modal';
import DataTable, { DataTableHeader } from '../../components/layout/DataTable';
import Botao from '../../components/Botao';
import BotaoPerigoso from '../../components/BotaoPerigoso';
import Icon from '@/components/Icon';
import {
  mdiCalendarAlert,
  mdiCalendarCheck,
  mdiCalendarClock,
  mdiCalendarHeart,
  mdiCalendarPlus,
  mdiCalendarRemove,
  mdiPen,
  mdiPlus,
  mdiTrashCan,
} from '@mdi/js';

import { subscribeUserToPush } from '@/lib/webpush';
import { FormAtividade } from '../crm_atividades/FormAtividade';
import MiniCalendario from './MiniCalendario';
import { expedienteDoDia, formatDuracao, slotsLivres } from './slots';
import styles from './page.module.css';
import AlertBox from '@/components/layout/AlertBox';

const pubKey = import.meta.env.VITE_PUB_KEY;

type ResponseList = {
  ok?: boolean;
  result: any[];
  total: number;
  cursor: string | number | null;
};

const iconePorEstado: Record<string, { path: string; cor: string }> = {
  pendente: { path: mdiCalendarClock, cor: 'var(--tc-color-gray-400)' },
  confirmada: { path: mdiCalendarHeart, cor: 'var(--tc-color-success-500)' },
  concluida: { path: mdiCalendarCheck, cor: 'var(--tc-color-success-500)' },
  cancelada: { path: mdiCalendarRemove, cor: 'var(--tc-color-danger-500)' },
};

const EstadoAtividade = ({ estado }: { estado: string }) => {
  const info = iconePorEstado[estado];
  if (!info) return <span>{estado}</span>;
  return (
    <HBox gap="4px">
      <Icon path={info.path} size={0.85} color={info.cor} />
      <span>
        {estado === 'pendente' && 'Pendente'}
        {estado === 'confirmada' && 'Confirmada'}
        {estado === 'concluida' && 'Concluída'}
        {estado === 'cancelada' && 'Cancelada'}
      </span>
    </HBox>
  );
};

// Valores iniciais de uma nova atividade. `estado`, `avisar_minutos` e as datas
// são obrigatórios na criação (ver AtividadeValidator), por isso já vêm preenchidos.
// Quando `inicio`/`fim` não são passados (ex.: clique num slot livre), usa o
// dia selecionado às 09:00, ou a próxima hora cheia quando o dia é hoje.
const novaAtividade = (dia: string | null, inicio?: Date, fim?: Date) => {
  if (!inicio) {
    const base = dia ? new Date(`${dia}T00:00:00`) : new Date();
    inicio =
      dia && !isToday(base)
        ? new Date(`${dia}T09:00:00`)
        : addHours(startOfHour(new Date()), 1);
  }
  const fimFinal = fim ?? addHours(inicio, 1);

  return {
    titulo: '',
    natureza: 'reuniao',
    estado: 'pendente',
    avisar_minutos: 0,
    data_evento: format(inicio, "yyyy-MM-dd'T'HH:mm:ss"),
    data_fim_evento: format(fimFinal, "yyyy-MM-dd'T'HH:mm:ss"),
  };
};

const askPermission = () => {
  Notification.requestPermission().then(result => {
    if (result !== 'granted') {
      console.warn("We weren't granted permission.");
    } else {
      subscribeUserToPush(pubKey);
    }
  });
};

const Page = () => {
  const navigate = useNavigate();
  const [mes, setMes] = useState<Date>(() => new Date());
  const [diaSelecionado, setDiaSelecionado] = useState<string | null>(null);
  const [editObject, setEditObject] = useState<any>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string> | null>(null);

  const inicioGrade = useMemo(() => startOfWeek(startOfMonth(mes)), [mes]);
  const fimGrade = useMemo(() => endOfWeek(endOfMonth(mes)), [mes]);

  const queryList = useQuery({
    queryKey: ['atividade', 'crm_dashboard', format(mes, 'yyyy-MM')],
    queryFn: async () => {
      const params = new URLSearchParams({
        di: format(inicioGrade, "yyyy-MM-dd'T'00:00:00"),
        df: format(fimGrade, "yyyy-MM-dd'T'23:59:59"),
        t: '500',
      });

      const r = await axios.get<ResponseList>(`/api/atividade?${params.toString()}`, {
        withCredentials: true,
      });

      return r.data;
    },
  });

  const eventosPorDia = useMemo(() => {
    const mapa: Record<string, any[]> = {};
    for (const atividade of queryList.data?.result ?? []) {
      const chave = format(new Date(atividade.data_evento), 'yyyy-MM-dd');
      if (!mapa[chave]) mapa[chave] = [];
      mapa[chave].push({
        ...atividade,
        // O <input type="datetime-local"> não aceita valor com timezone (ex.: sufixo "Z");
        // reformata para o formato local esperado antes de abrir no formulário de edição.
        data_evento: format(new Date(atividade.data_evento), "yyyy-MM-dd'T'HH:mm:ss"),
        data_fim_evento: atividade.data_fim_evento
          ? format(new Date(atividade.data_fim_evento), "yyyy-MM-dd'T'HH:mm:ss")
          : atividade.data_fim_evento,
      });
    }
    return mapa;
  }, [queryList.data]);

  const eventosDoDia = diaSelecionado ? eventosPorDia[diaSelecionado] ?? [] : [];

  // Agendas do usuário com seus horários (`agenda_horario`), para calcular o
  // expediente do dia selecionado e destacar os intervalos livres na lista.
  const queryAgendas = useQuery({
    queryKey: ['agenda', 'crm_dashboard_expediente'],
    queryFn: async () => {
      const r = await axios.get('/api/agenda', { withCredentials: true });
      return r.data;
    },
  });
  const agendas = queryAgendas.data?.result ?? [];

  const slotsLivresDoDia = useMemo(() => {
    if (!diaSelecionado) return [];

    const dia = new Date(`${diaSelecionado}T00:00:00`);
    const { janelas, duracaoMinimaMin } = expedienteDoDia(agendas, dia);
    if (janelas.length === 0) return [];

    // Atividades canceladas não devem "prender" o horário como ocupado.
    const ocupados = eventosDoDia
      .filter(e => e.estado !== 'cancelada')
      .map(e => {
        const inicio = new Date(e.data_evento);
        const fim = e.data_fim_evento ? new Date(e.data_fim_evento) : addHours(inicio, 1);
        return { inicio, fim };
      });

    return slotsLivres(janelas, ocupados, duracaoMinimaMin);
  }, [diaSelecionado, agendas, eventosDoDia]);

  const linhasDoDia = useMemo(() => {
    const eventos = eventosDoDia.map(e => ({ ...e, __ordenacao: new Date(e.data_evento).getTime() }));
    const livres = slotsLivresDoDia.map(s => ({
      __livre: true,
      inicio: s.inicio,
      fim: s.fim,
      __ordenacao: s.inicio.getTime(),
    }));
    return [...eventos, ...livres].sort((a, b) => a.__ordenacao - b.__ordenacao);
  }, [eventosDoDia, slotsLivresDoDia]);

  const changeMutator = useMutation(
    async (data: any) => {
      if (!data) return;
      setFieldErrors(null);

      if (data.id) {
        return await axios.put(`/api/atividade/${data.id}`, data, { withCredentials: true });
      }
      return await axios.post(`/api/atividade`, data, { withCredentials: true });
    },
    {
      onSuccess: (_res, vars: any) => {
        setFieldErrors(null);
        setEditObject(null);

        // Mantém visível a atividade recém-salva, mesmo que tenha sido marcada
        // para outro dia (ou outro mês) que não o selecionado no calendário.
        if (vars?.data_evento) {
          const data = new Date(vars.data_evento);
          if (!isSameMonth(data, mes)) setMes(startOfMonth(data));
          setDiaSelecionado(format(data, 'yyyy-MM-dd'));
        }

        queryList.refetch();
        toast(vars?.id > 0 ? 'Atividade alterada com sucesso' : 'Atividade adicionada com sucesso', {
          toastId: 'success',
          position: 'top-center',
          type: 'success',
          autoClose: 5000,
        });

        if (vars?.avisar_minutos > 0) {
          askPermission();
        }
      },
      onError: (err: any) => {
        if (err.response?.status === 422) {
          setFieldErrors(err.response?.data?.fieldErrors ?? {});
        }
        toast(err.response?.data?.msg ?? 'Erro desconhecido', {
          toastId: 'error',
          position: 'top-center',
          type: 'warning',
          autoClose: 5000,
        });
      },
    },
  );

  const deleteMutator = useMutation(
    async (id: number) => {
      return await axios.delete(`/api/atividade/${id}`, { withCredentials: true });
    },
    {
      onSuccess: () => {
        toast('Atividade apagada com sucesso', {
          toastId: 'success',
          position: 'top-center',
          type: 'success',
          autoClose: 5000,
        });
        setEditObject(null);
      },
      onError: () => {
        toast('Ocorreu um erro ao tentar apagar a atividade.', {
          toastId: 'error',
          position: 'top-center',
          type: 'warning',
          autoClose: 5000,
        });
      },
      onSettled: () => {
        queryList.refetch();
      },
    },
  );

  const handleChangeMes = useCallback((novoMes: Date) => {
    setMes(novoMes);
    setDiaSelecionado(null);
  }, []);

  const handleCloseModal = () => {
    setEditObject(null);
    setFieldErrors(null);
  };

  const handleNovaAtividade = useCallback(() => {
    setFieldErrors(null);
    setEditObject(novaAtividade(diaSelecionado));
  }, [diaSelecionado]);

  const handleNovaAtividadeSlot = useCallback(
    (inicio: Date, fim: Date) => {
      setFieldErrors(null);
      setEditObject(novaAtividade(diaSelecionado, inicio, fim));
    },
    [diaSelecionado],
  );

  const confirmacaoApagar = (row: any) =>
    row.integracao_event_id
      ? 'Tem certeza? A atividade será apagada do calendário externo também.'
      : 'Deseja realmente apagar esta atividade?';

  const headers: DataTableHeader[] = [
    {
      label: 'Horário',
      width: '140px',
      render: row =>
        row.__livre
          ? `${format(row.inicio, 'HH:mm')}–${format(row.fim, 'HH:mm')}`
          : format(new Date(row.data_evento), 'HH:mm'),
    },
    {
      label: 'Título',
      render: row =>
        row.__livre ? (
          isBefore(row.fim, new Date()) ? (
            <span className={styles.spanLivre}>Livre · {formatDuracao(row.fim.getTime() - row.inicio.getTime())}</span>
          ) : (
            <button
              type="button"
              className={styles.linkLivre}
              onClick={() => handleNovaAtividadeSlot(row.inicio, row.fim)}
            >
              Livre · {formatDuracao(row.fim.getTime() - row.inicio.getTime())}{" "}
              <Icon path={mdiCalendarPlus} size={1} color="var(--tc-color-primary-300)" />
            </button>
          )
        ) : (
          <button
            type="button"
            className={styles.linkTitulo}
            onClick={() => setEditObject(row)}
          >
            {row.titulo}
          </button>
        ),
    },
    {
      label: 'Cliente',
      render: row => (row.__livre ? '' : row.cliente?.nome ?? '-'),
    },
    {
      label: 'Estado',
      width: '140px',
      render: row => (row.__livre ? '' : <EstadoAtividade estado={row.estado} />),
    },
    {
      label: 'Ações',
      width: '100px',
      alignRight: true,
      render: row =>
        row.__livre ? null : (
          <HBox gap="4px" className={styles.acoesLinha}>
            <Botao
              variant="none-secondary"
              icon={mdiPen}
              title="Editar atividade"
              onClick={() => setEditObject(row)}
            />
            <BotaoPerigoso
              variant="none-danger"
              icon={mdiTrashCan}
              title="Apagar atividade"
              buttonProps={{ title: 'Apagar atividade' }}
              onClick={() => deleteMutator.mutate(row.id)}
              confirmation={confirmacaoApagar(row)}
            />
          </HBox>
        ),
    },
  ];

  return (
    <LayoutUsuario>
      <Modal
        instanceId="modal-atividade-dashboard"
        title={editObject?.id > 0 ? 'Editar atividade' : 'Nova atividade'}
        show={!!editObject}
        onClose={handleCloseModal}
        onCancel={handleCloseModal}
        onConfirm={() => changeMutator.mutate(editObject)}
      >
        <FormAtividade value={editObject} onChange={setEditObject} fieldErrors={fieldErrors} />
        {editObject?.id > 0 && (
          <div style={{ textAlign: 'end' }}>
            <BotaoPerigoso
              icon={mdiTrashCan}
              onClick={() => deleteMutator.mutate(editObject?.id)}
              variant="danger"
              confirmation={confirmacaoApagar(editObject)}
            >
              Apagar
            </BotaoPerigoso>
          </div>
        )}
      </Modal>

      <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
        <HBox>
          <VBox>
            <Titulo>Dashboard</Titulo>
            <Descricao>Visão geral rápida da sua agenda e atividades.</Descricao>
          </VBox>
          <HBox className={styles.headerAcoes}>
            <Botao
              variant="primary"
              icon={mdiPlus}
              onClick={handleNovaAtividade}
              title={
                diaSelecionado
                  ? `Nova atividade em ${format(new Date(`${diaSelecionado}T00:00:00`), 'dd/MM/yyyy')}`
                  : 'Nova atividade'
              }
            >
              Nova atividade
            </Botao>
          </HBox>
        </HBox>

        {!queryAgendas.isLoading && agendas.length === 0 && (
          <AlertBox variant="info" style={{ padding: '16px' }}>
            <HBox gap="12px" style={{ alignItems: 'center' }}>
              <Icon path={mdiCalendarAlert} size={1.2} color="var(--tc-color-warning-500)" />
              <VBox gap="0" style={{ flex: 1 }}>
                <strong>Você ainda não tem nenhuma agenda cadastrada</strong>
                <Descricao>
                  Cadastre uma agenda para calcular seu expediente e horários livres aqui no dashboard.
                </Descricao>
              </VBox>
              <HBox gap="8px" style={{ width: 'auto' }}>
                <Botao
                  variant="secondary"
                  onClick={() => navigate('/agenda')}
                >
                  Ver agendas
                </Botao>
                <Botao
                  variant="primary"
                  icon={mdiCalendarPlus}
                  onClick={() => navigate('/agenda', { state: { abrirNova: true } })}
                >
                  Adicionar agenda
                </Botao>
              </HBox>
            </HBox>
          </AlertBox>
        )}

        <Grid>
          <GridItem sm={12} lg={4}>
            <Card style={{ justifyContent: 'center', alignItems: 'center' }}>
              <MiniCalendario
                mes={mes}
                onChangeMes={handleChangeMes}
                eventosPorDia={eventosPorDia}
                diaSelecionado={diaSelecionado}
                onSelectDia={setDiaSelecionado}
              />
            </Card>
          </GridItem>

          <GridItem sm={12} lg={8}>
            <Card>
              <VBox gap="12px">
                <Titulo>
                  {diaSelecionado
                    ? format(new Date(`${diaSelecionado}T00:00:00`), "d 'de' MMMM 'de' yyyy", { locale: ptBR })
                    : 'Selecione um dia'}
                </Titulo>

                {diaSelecionado && linhasDoDia.length === 0 && (
                  <Descricao>Nenhum evento neste dia.</Descricao>
                )}

                {diaSelecionado && linhasDoDia.length > 0 && (
                  <div className={styles.tabelaContainer}>
                    <DataTable
                      cabecalhos={headers}
                      linhas={linhasDoDia}
                      isLoading={queryList.isFetching}
                    />
                  </div>
                )}
              </VBox>
            </Card>
          </GridItem>
        </Grid>
      </VBox>
    </LayoutUsuario>
  );
};

export default Page;
